import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Typography,
  IconButton,
  Stack,
  LinearProgress,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

const FileDropzone = ({
  accept,
  maxFiles = 1,
  maxSize = 10485760, // 10MB
  onFilesChange,
  title,
  description,
  value = [],
  showPreview = true,
  previewType = 'image',
}) => {
  const [uploadProgress, setUploadProgress] = useState({});

  const onDrop = useCallback((acceptedFiles) => {
    // Simulate upload progress
    acceptedFiles.forEach(file => {
      setUploadProgress(prev => ({
        ...prev,
        [file.name]: 0
      }));
      
      let progress = 0;
      const interval = setInterval(() => {
        progress += 10;
        setUploadProgress(prev => ({
          ...prev,
          [file.name]: progress
        }));
        if (progress >= 100) {
          clearInterval(interval);
        }
      }, 100);
    });

    onFilesChange([...value, ...acceptedFiles]);
  }, [onFilesChange, value]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxFiles: maxFiles - value.length,
    maxSize,
    disabled: value.length >= maxFiles,
  });

  const handleRemove = (fileToRemove) => {
    onFilesChange(value.filter(file => file !== fileToRemove));
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileToRemove.name];
      return newProgress;
    });
  };

  const renderPreview = (file) => {
    if (!showPreview) return null;

    if (previewType === 'image' && file.type.startsWith('image/')) {
      return (
        <Box
          component="img"
          src={URL.createObjectURL(file)}
          alt={file.name}
          sx={{
            width: 60,
            height: 60,
            borderRadius: 1,
            objectFit: 'cover',
          }}
        />
      );
    }

    return (
      <Box
        sx={{
          width: 60,
          height: 60,
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        {file.type.startsWith('image/') ? (
          <ImageIcon sx={{ fontSize: 30 }} />
        ) : (
          <InsertDriveFileIcon sx={{ fontSize: 30 }} />
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'divider',
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          cursor: value.length >= maxFiles ? 'default' : 'pointer',
          bgcolor: isDragActive ? 'action.hover' : 'background.paper',
          transition: 'all 0.2s ease',
          '&:hover': value.length >= maxFiles ? {} : {
            borderColor: 'primary.main',
            bgcolor: 'action.hover',
          },
        }}
      >
        <input {...getInputProps()} />
        <CloudUploadIcon sx={{ fontSize: 40, mb: 2, color: 'primary.main' }} />
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {value.length >= maxFiles
            ? 'Maximum files reached'
            : description || 'Drag and drop files here, or click to select files'}
        </Typography>
      </Box>

      {value.length > 0 && (
        <Stack spacing={2} sx={{ mt: 2 }}>
          {value.map((file, index) => (
            <Box
              key={file.name}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 2,
                borderRadius: 1,
                bgcolor: 'background.paper',
                boxShadow: 1,
              }}
            >
              {renderPreview(file)}
              <Box sx={{ ml: 2, flex: 1 }}>
                <Typography variant="subtitle2" noWrap>
                  {file.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
                {uploadProgress[file.name] !== undefined && uploadProgress[file.name] < 100 && (
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress[file.name]}
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>
              <IconButton
                size="small"
                onClick={() => handleRemove(file)}
                sx={{ ml: 1 }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default FileDropzone;
